import { signal } from '@preact/signals';
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n.js';
import { onUploadNoteImage } from '../actions.js';
import { noteImageUrl } from '../thumbs.js';
import { IconLink, IconMedia, IconTrash, IconUpload } from './icons.js';
import { openMediaPicker } from './media-picker.js';
import { Popover } from './popover.js';
import type { EditorCommand, NoteEditorHandle } from './note-editor.js';

/**
 * **Le montage Preact de la surface d'édition** — et la seule chose lourde de ce
 * fichier est ce qu'il **n'importe pas**.
 *
 * `note-editor.ts` pèse ~200 ko une fois compressé : il est chargé par un
 * `import()` **dynamique**, à l'ouverture d'une note et pas avant (décision E2).
 * Le seul lien statique vers lui est un `import type`, qui disparaît à la
 * compilation. Rompre cette règle ne casse rien — cela déplace simplement deux
 * cents kilo-octets sur le premier écran, sans que rien ne le dise.
 *
 * Deux règles de construction survivent à l'éditeur maison, mot pour mot :
 *
 * ① **Preact ne rediffuse jamais le contenu de la zone d'édition.** Le
 *    `dangerouslySetInnerHTML` constant est ce qui le lui interdit : le panneau
 *    se redessine à chaque frappe — la pastille « Modifié » en dépend —, et un
 *    rendu réécrirait le document sous le curseur. TipTap monte **impérativement**
 *    dans cet élément, sans passer par Preact.
 * ② **Le document n'est rechargé que lorsqu'il change pour une raison extérieure
 *    au clavier** : ouverture, relecture après conflit, restauration d'une
 *    révision, retour depuis le mode source. C'est ce que compte `docKey`.
 */

/**
 * **L'éditeur monté, s'il y en a un.**
 *
 * Il vit dans un signal de module plutôt que dans une prop, parce que la barre de
 * mise en forme et la zone d'édition sont **deux sous-arbres séparés** du panneau
 * (la barre est au-dessus de `.editor-stage`), et qu'il n'y a jamais qu'une note
 * ouverte à la fois — l'éditeur est une surcouche modale.
 */
const mounted = signal<NoteEditorHandle | undefined>(undefined);

/**
 * Ce que le curseur porte a pu changer. La barre s'y abonne : sans ce compteur,
 * elle n'aurait aucun état, et douze touches identiques quelle que soit la
 * position du curseur — le retour d'usage du 2026-08-09 disait exactement cela.
 */
const cursor = signal(0);

/** Rend le focus à la note ; le panneau s'en sert au changement de mode. */
export function focusRichNote(): void {
  mounted.value?.focus();
}

export function RichNote({
  markdown,
  docKey,
  onInput,
  ariaLabel,
}: {
  readonly markdown: string;
  /** Change **uniquement** quand le texte vient d'ailleurs que du clavier. */
  readonly docKey: number;
  readonly onInput: (markdown: string) => void;
  readonly ariaLabel: string;
}): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  /**
   * Le module arrive **après** le premier rendu : ce qu'il lira au montage doit
   * être le texte du moment, pas celui capturé à la première passe d'effet.
   */
  const latest = useRef({ markdown, onInput });
  latest.current = { markdown, onInput };

  useEffect(() => {
    const node = host.current;
    if (!node) return;
    let live = true;
    let made: NoteEditorHandle | undefined;

    void import('./note-editor.js').then(({ createNoteEditor }) => {
      if (!live || !host.current) return;
      made = createNoteEditor(host.current, {
        markdown: latest.current.markdown,
        ariaLabel,
        onChange: (text) => latest.current.onInput(text),
        onSelection: () => {
          cursor.value += 1;
        },
        // **C'est ici que le module d'édition rencontre l'API, et nulle part
        // ailleurs.** Il ne connaît ni le lot de présignature, ni le compte : il
        // reçoit une fonction qui rend une URL, ce qui est aussi ce qui permet au
        // banc de le charger seul et d'y mesurer les deux cas — avec et sans.
        resolveMedia: (fid) => noteImageUrl(fid),
      });
      mounted.value = made;
      made.focus();
    });

    return () => {
      live = false;
      if (mounted.peek() === made) mounted.value = undefined;
      made?.destroy();
    };
  }, []);

  useEffect(() => {
    // Au montage, l'éditeur n'existe pas encore et reçoit le texte à sa
    // création : ce `undefined` n'est pas un oubli, c'est le cas normal.
    mounted.value?.setMarkdown(markdown);
  }, [docKey]);

  return (
    <div
      class="wysiwyg"
      data-testid="editor-rich"
      ref={host}
      // Preact ne doit **jamais** rediffuser les enfants : la valeur est
      // constante, donc il ne réécrit rien après le premier rendu, et le
      // document reste celui que TipTap a monté.
      dangerouslySetInnerHTML={EMPTY}
    />
  );
}

const EMPTY = { __html: '' } as const;

/**
 * La barre de mise en forme. Elle porte **ce qui a une syntaxe** dans le
 * sous-ensemble de `vision.md` §2.4, et rien de plus : pas de couleur, pas de
 * taille, pas d'alignement — trois choses que le Markdown ne sait pas écrire et
 * qui se perdraient au premier enregistrement.
 *
 * Les `data-testid` sont **ceux d'avant**, à la touche près : les parcours écrits
 * les visent, et un remplacement d'éditeur qui les renommerait ferait passer une
 * régression pour une suite à réécrire.
 */
export function FormatBar(): JSX.Element {
  const editor = mounted.value;
  // Lu pour l'abonnement : la barre se redessine quand le curseur bouge.
  void cursor.value;

  /**
   * **Le champ d'adresse.** Poser un lien demande une adresse, donc un champ,
   * donc un focus qui quitte la zone d'édition. Avec TipTap ce n'est plus un
   * piège : la sélection vit dans l'état du document, pas dans celle du
   * navigateur — c'est ce qui fait disparaître le clonage d'intervalle que
   * l'éditeur maison devait faire à la main.
   */
  const [link, setLink] = useState<{ url: string; existing: boolean } | undefined>(undefined);

  const on = (command: EditorCommand): boolean => editor?.isActive(command) ?? false;
  const run = (command: EditorCommand): void => editor?.run(command);

  const openLink = (): void => {
    if (link) return setLink(undefined);
    if (!editor) return;
    const existing = editor.linkAt();
    setLink({ url: existing ?? '', existing: existing !== undefined });
  };

  const applyLink = (): void => {
    const url = (link?.url ?? '').trim();
    if (url === '') return setLink(undefined);
    editor?.setLink(url);
    setLink(undefined);
  };

  const dropLink = (): void => {
    run('unlink');
    setLink(undefined);
  };

  return (
    <>
      <div class="format-bar" role="toolbar" aria-label={t('editor.format')}>
        <Key testId="fmt-bold" label={t('editor.bold')} on={on('bold')} onPress={() => run('bold')}>
          <strong>B</strong>
        </Key>
        <Key
          testId="fmt-italic"
          label={t('editor.italic')}
          on={on('italic')}
          onPress={() => run('italic')}
        >
          <em>I</em>
        </Key>
        <Key
          testId="fmt-strike"
          label={t('editor.strike')}
          on={on('strike')}
          onPress={() => run('strike')}
        >
          <s>S</s>
        </Key>
        <span class="format-sep" aria-hidden="true" />
        <Key
          testId="fmt-h1"
          label={t('editor.heading', { level: 1 })}
          on={on('h1')}
          onPress={() => run('h1')}
        >
          H1
        </Key>
        <Key
          testId="fmt-h2"
          label={t('editor.heading', { level: 2 })}
          on={on('h2')}
          onPress={() => run('h2')}
        >
          H2
        </Key>
        <Key
          testId="fmt-p"
          label={t('editor.paragraph')}
          on={on('paragraph')}
          onPress={() => run('paragraph')}
        >
          ¶
        </Key>
        <span class="format-sep" aria-hidden="true" />
        <Key
          testId="fmt-ul"
          label={t('editor.bulletList')}
          on={on('bulletList')}
          onPress={() => run('bulletList')}
        >
          •
        </Key>
        <Key
          testId="fmt-ol"
          label={t('editor.numberList')}
          on={on('orderedList')}
          onPress={() => run('orderedList')}
        >
          1.
        </Key>
        <Key
          testId="fmt-todo"
          label={t('editor.todo')}
          on={on('taskList')}
          onPress={() => run('taskList')}
        >
          ☑
        </Key>
        <Key
          testId="fmt-quote"
          label={t('editor.quote')}
          on={on('quote')}
          onPress={() => run('quote')}
        >
          ❝
        </Key>
        <Key
          testId="fmt-code"
          label={t('editor.codeBlock')}
          on={on('codeBlock')}
          onPress={() => run('codeBlock')}
        >
          {'{ }'}
        </Key>
        <Key testId="fmt-hr" label={t('editor.rule')} onPress={() => run('rule')}>
          —
        </Key>
        <Key testId="fmt-link" label={t('editor.link')} on={link !== undefined} onPress={openLink}>
          <IconLink size={16} />
        </Key>
        {/* **La table s'insère, elle ne se dessine pas.** Trois lignes sur trois
            colonnes, **rangée d'en-tête comprise** : le sous-ensemble de
            `notes.md` §1 n'a pas d'autre forme de table, et une table sans sa
            ligne de tirets n'en est pas une pour le lecteur. Ce qu'on ajoute ou
            retire ensuite est dans la barre contextuelle. */}
        <Key
          testId="fmt-table"
          label={t('editor.table')}
          on={on('table')}
          onPress={() => run('table')}
        >
          ▦
        </Key>
        <ImageKey />
      </div>

      <ContextBar />

      {link ? (
        <div class="link-form" data-testid="link-form">
          <input
            type="url"
            class="input"
            data-testid="link-url"
            inputMode="url"
            placeholder="https://…"
            aria-label={t('editor.linkUrl')}
            autoFocus
            value={link.url}
            onInput={(ev) => setLink({ ...link, url: (ev.currentTarget as HTMLInputElement).value })}
            onKeyDown={(ev) => {
              const key = (ev as KeyboardEvent).key;
              if (key === 'Enter') {
                ev.preventDefault();
                applyLink();
              }
              // L'éditeur ferme sur `Échap` : sans cet arrêt, annuler le champ
              // fermerait la note entière.
              if (key === 'Escape') {
                ev.stopPropagation();
                setLink(undefined);
              }
            }}
          />
          <button type="button" class="btn primary" data-testid="link-apply" onClick={applyLink}>
            {t('editor.linkApply')}
          </button>
          {link.existing ? (
            <button type="button" class="btn quiet" data-testid="link-remove" onClick={dropLink}>
              {t('editor.linkRemove')}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/**
 * **Les deux façons d'illustrer une note** *(N2, 2026-08-17)*, derrière une seule
 * touche.
 *
 * Elles sont deux parce que ce sont deux gestes différents, pas deux chemins vers
 * le même : « celle que j'ai déjà » ouvre l'espace, « celle qui est sur cet
 * appareil » l'y ajoute d'abord. Les distinguer *après* le clic aurait demandé un
 * écran qui pose la question, c'est-à-dire un geste de plus dans les deux cas.
 *
 * L'envoi passe par un `<input type="file">` caché plutôt que par une API : c'est
 * la seule façon d'ouvrir le sélecteur du système, et sur téléphone `accept` y
 * fait apparaître la photothèque et l'appareil photo.
 */
function ImageKey(): JSX.Element {
  const file = useRef<HTMLInputElement>(null);

  const insert = (fid: string, alt: string): void => mounted.peek()?.insertImage(fid, alt);

  const chosen = (event: JSX.TargetedEvent<HTMLInputElement>): void => {
    const input = event.currentTarget;
    const picked = input.files?.[0];
    // Le champ se vide **toujours** : sans cela, choisir deux fois le même
    // fichier n'émettrait pas de second `change`, et le geste paraîtrait ignoré.
    input.value = '';
    if (!picked) return;
    void onUploadNoteImage(picked).then((fid) => {
      if (fid) insert(fid, picked.name);
    });
  };

  return (
    <>
      <Popover
        testId="fmt-image"
        label={t('editor.image')}
        width={236}
        align="end"
        buttonClass="fmt-key"
        trigger={() => <IconMedia size={16} />}
      >
        {(close) => (
          <>
            <button
              type="button"
              role="menuitem"
              data-testid="image-from-space"
              onClick={() => {
                close();
                openMediaPicker(({ fid, name }) => insert(fid, name));
              }}
            >
              <IconMedia size={18} />
              <span>{t('editor.imageFromSpace')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid="image-from-file"
              onClick={() => {
                close();
                file.current?.click();
              }}
            >
              <IconUpload size={18} />
              <span>{t('editor.imageUpload')}</span>
            </button>
          </>
        )}
      </Popover>
      <input
        ref={file}
        type="file"
        class="visually-hidden"
        data-testid="image-file"
        accept="image/*"
        onChange={chosen}
      />
    </>
  );
}

/**
 * **La barre contextuelle** — ce qui ne s'applique qu'à l'endroit où l'on est.
 *
 * Elle porte les gestes de table et le texte de remplacement d'une image, et elle
 * n'existe que quand l'un des deux a un sens. C'est ce qui a permis de ne pas
 * ajouter sept touches de table à une barre principale qui débordait déjà à
 * 390 px — le grief exact du 2026-08-14 sur la barre de l'éditeur.
 *
 * **L'image gagne sur la table** quand les deux s'appliquent : une image dans une
 * cellule est ce qu'on vient de sélectionner, la table est l'endroit où elle se
 * trouve. Le plus précis passe devant.
 */
function ContextBar(): JSX.Element | null {
  const editor = mounted.value;
  // Lu pour l'abonnement, comme la barre principale.
  void cursor.value;

  const image = editor?.imageAt();
  if (image) return <ImageBar alt={image.alt} />;
  if (editor?.inTable() !== true) return null;

  const run = (command: EditorCommand): void => editor.run(command);

  return (
    <div class="format-bar context-bar" role="toolbar" aria-label={t('editor.tableTools')}>
      <span class="format-label">{t('editor.table')}</span>
      <Key testId="tbl-row-before" label={t('editor.rowBefore')} onPress={() => run('rowBefore')}>
        ↑+
      </Key>
      <Key testId="tbl-row-after" label={t('editor.rowAfter')} onPress={() => run('rowAfter')}>
        ↓+
      </Key>
      <Key testId="tbl-col-before" label={t('editor.colBefore')} onPress={() => run('colBefore')}>
        ←+
      </Key>
      <Key testId="tbl-col-after" label={t('editor.colAfter')} onPress={() => run('colAfter')}>
        →+
      </Key>
      <span class="format-sep" aria-hidden="true" />
      <Key testId="tbl-drop-row" label={t('editor.dropRow')} onPress={() => run('dropRow')}>
        ⊟
      </Key>
      <Key testId="tbl-drop-col" label={t('editor.dropCol')} onPress={() => run('dropCol')}>
        ⊞
      </Key>
      <Key testId="tbl-drop" label={t('editor.dropTable')} onPress={() => run('dropTable')}>
        <IconTrash size={16} />
      </Key>
    </div>
  );
}

/**
 * **Le texte de remplacement s'écrit ici**, et c'est le seul endroit du produit
 * où il s'écrit.
 *
 * Il n'est pas décoratif : c'est ce qui s'affiche quand le média a disparu, quand
 * l'accès a été retiré, ou quand la note est lue par quelqu'un qui n'entend pas
 * l'image. Le sélecteur y met le nom du fichier — honnête à défaut d'être bon —,
 * et cette barre est ce qui permet de le remplacer par une phrase.
 *
 * Le champ écrit **sans reprendre le focus** : `updateAttributes` sans `focus()`.
 * L'inverse rendrait le curseur au document à chaque lettre, et le champ se
 * viderait à la première frappe.
 */
function ImageBar({ alt }: { readonly alt: string }): JSX.Element {
  return (
    <div class="format-bar context-bar" role="toolbar" aria-label={t('editor.imageTools')}>
      <span class="format-label">{t('editor.image')}</span>
      <input
        type="text"
        class="input grow"
        data-testid="image-alt"
        placeholder={t('editor.imageAltHint')}
        aria-label={t('editor.imageAlt')}
        value={alt}
        onInput={(ev) => mounted.peek()?.setImageAlt((ev.currentTarget as HTMLInputElement).value)}
        onKeyDown={(ev) => {
          // L'éditeur ferme sur `Échap` : sans cet arrêt, quitter le champ
          // fermerait la note entière — même règle que le champ d'adresse.
          if ((ev as KeyboardEvent).key === 'Escape') ev.stopPropagation();
        }}
      />
      <Key testId="image-remove" label={t('editor.imageRemove')} onPress={() => mounted.peek()?.run('dropImage')}>
        <IconTrash size={16} />
      </Key>
    </div>
  );
}

function Key({
  testId,
  label,
  onPress,
  on = false,
  children,
}: {
  readonly testId: string;
  readonly label: string;
  readonly onPress: () => void;
  /** **Ce que le curseur porte réellement**, jamais ce qu'on vient de cliquer. */
  readonly on?: boolean;
  readonly children: JSX.Element | string;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      class={on ? 'on' : undefined}
      aria-pressed={on}
      title={label}
      aria-label={label}
      // **Le bouton ne prend pas le focus.** La sélection de ProseMirror survit
      // au blur, mais le curseur clignotant, lui, ne revient pas — et une barre
      // qui fait perdre sa place à chaque touche ne s'utilise pas.
      onMouseDown={(ev) => ev.preventDefault()}
      onClick={onPress}
    >
      {children}
    </button>
  );
}
